// src/redux-saga/sagas/knowledgeBaseSaga.js

import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import {
  FETCH_KB_REQUEST,
  fetchKbSuccess,
  fetchKbFailure,
  CREATE_KB_REQUEST,
  createKbSuccess,
  createKbFailure,
  DELETE_KB_REQUEST,
  deleteKbSuccess,
  deleteKbFailure,
  UPDATE_KB_REQUEST,
  updateKbSuccess,
  updateKbFailure,
  FETCH_KB_FILES_REQUEST,
  fetchKbFilesSuccess,
  fetchKbFilesFailure,
  FETCH_KB_FILE_CONTENT_REQUEST,
  fetchKbFileContentSuccess,
  fetchKbFileContentFailure,
  UPLOAD_FILES_REQUEST,
  uploadFilesSuccess,
  uploadFilesFailure,
  DELETE_KB_FILE_REQUEST,
  deleteKbFileSuccess,
  deleteKbFileFailure,
  FETCH_KB_SITES_REQUEST,
  fetchKbSitesSuccess,
  fetchKbSitesFailure,
  FETCH_SITE_CONTENT_REQUEST,
  fetchSiteContentSuccess,
  fetchSiteContentFailure,
  ADD_KB_SITE_REQUEST,
  addKbSiteSuccess,
  addKbSiteFailure,
  DELETE_KB_SITE_REQUEST,
  deleteKbSiteSuccess,
  deleteKbSiteFailure,
  DOWNLOAD_FILE_REQUEST,
  downloadFileSuccess,
  downloadFileFailure,
} from '../actions/knowledgeBaseActions';
import { getBaseUrl } from '../utils/getBaseUrl';

const API_BASE_URL = getBaseUrl();
const getAuthToken = () => localStorage.getItem('authToken');

// Helper function to create axios config with token
const createConfig = () => ({
  headers: { Authorization: `Bearer ${getAuthToken()}` },
});

// Worker Saga: Fetch Knowledge Bases
function* fetchKnowledgeBases() {
  try {
    const response = yield call(axios.get, `${API_BASE_URL}/knowledge-base`, createConfig());
    yield put(fetchKbSuccess(response.data));
  } catch (error) {
    yield put(fetchKbFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Create Knowledge Base
function* createKnowledgeBase(action) {
  try {
    const response = yield call(
      axios.post,
      `${API_BASE_URL}/knowledge-base`,
      action.payload,
      createConfig()
    );
    yield put(createKbSuccess(response.data));
  } catch (error) {
    yield put(createKbFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Delete Knowledge Base
function* deleteKnowledgeBase(action) {
  try {
    yield call(
      axios.delete,
      `${API_BASE_URL}/knowledge-base/${action.payload}`,
      createConfig()
    );
    yield put(deleteKbSuccess(action.payload));
  } catch (error) {
    yield put(deleteKbFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Update Knowledge Base
function* updateKnowledgeBase(action) {
  try {
    const { id, data } = action.payload;
    const response = yield call(
      axios.put,
      `${API_BASE_URL}/knowledge-base/${id}`,
      data,
      createConfig()
    );
    yield put(updateKbSuccess(response.data));
  } catch (error) {
    yield put(updateKbFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Fetch Files of a Knowledge Base
function* fetchKnowledgeBaseFiles(action) {
  try {
    const response = yield call(
      axios.get,
      `${API_BASE_URL}/knowledge-base/files/${action.payload}`,
      createConfig()
    );
    yield put(fetchKbFilesSuccess(action.payload, response.data));
  } catch (error) {
    yield put(fetchKbFilesFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Fetch File Content
function* fetchFileContent(action) {
  try {
    const response = yield call(
      axios.get,
      `${API_BASE_URL}/knowledge-base/files/content/${action.payload}`,
      createConfig()
    );
    yield put(fetchKbFileContentSuccess(response.data));
  } catch (error) {
    yield put(fetchKbFileContentFailure(error.response?.data?.message || error.message));
  }
}



function* downloadFileSaga(action) {
  try {
    const response = yield call(
      axios.get,
      `${API_BASE_URL}/knowledge-base/files/download/${action.payload}`,
      {
        ...createConfig(),
        responseType: 'blob', // To handle binary data
      }
    );

    // Create a blob URL for the downloaded file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Extract the filename from the content-disposition header
    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'downloaded_file';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match && match[1]) {
        fileName = match[1];
      }
    }
    link.setAttribute('download', fileName);

    document.body.appendChild(link);
    link.click();

    // Remove the link after download
    link.parentNode.removeChild(link);

    yield put(downloadFileSuccess({ url, fileName }));
  } catch (error) {
    yield put(downloadFileFailure(error.response?.data?.message || error.message));
  }
}
// Worker Saga: Upload Files
function* uploadFiles(action) {
  try {
    const response = yield call(
      axios.post,
      `${API_BASE_URL}/knowledge-base/files`,
      action.payload,
      {
        ...createConfig(),
        headers: {
          ...createConfig().headers,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    const kbId = action.payload.get('knowledgeId');
    yield put(uploadFilesSuccess(kbId, response.data));
  } catch (error) {
    yield put(uploadFilesFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Delete a File
function* deleteKnowledgeBaseFile(action) {
  try {
    const { fileId, kbId } = action.payload;
    yield call(
      axios.delete,
      `${API_BASE_URL}/knowledge-base/files/${fileId}`,
      createConfig()
    );
    yield put(deleteKbFileSuccess(kbId, fileId));
  } catch (error) {
    yield put(deleteKbFileFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Fetch Sites of a Knowledge Base
function* fetchKnowledgeBaseSites(action) {
  try {
    const response = yield call(
      axios.get,
      `${API_BASE_URL}/knowledge-base/sites/${action.payload}`,
      createConfig()
    );
    yield put(fetchKbSitesSuccess(action.payload, response.data));
  } catch (error) {
    yield put(fetchKbSitesFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Fetch Site Content
function* fetchSiteContent(action) {
  try {
    const response = yield call(
      axios.get,
      `${API_BASE_URL}/knowledge-base/sites/content/${action.payload}`,
      createConfig()
    );
    yield put(fetchSiteContentSuccess(response.data));
  } catch (error) {
    yield put(fetchSiteContentFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Add a Site
function* addKnowledgeBaseSite(action) {
  try {
    const response = yield call(
      axios.post,
      `${API_BASE_URL}/knowledge-base/sites/links`,
      action.payload,
      createConfig()
    );
    const { knowledgeBaseId } = action.payload;
    yield put(addKbSiteSuccess(knowledgeBaseId, response.data));
  } catch (error) {
    yield put(addKbSiteFailure(error.response?.data?.message || error.message));
  }
}

// Worker Saga: Delete a Site
function* deleteKnowledgeBaseSite(action) {
  try {
    const { siteId, kbId } = action.payload;
    yield call(
      axios.delete,
      `${API_BASE_URL}/knowledge-base/sites/${siteId}`,
      createConfig()
    );
    yield put(deleteKbSiteSuccess(kbId, siteId));
  } catch (error) {
    yield put(deleteKbSiteFailure(error.response?.data?.message || error.message));
  }
}

// Watcher Saga
function* knowledgeBaseSaga() {
  yield takeLatest(FETCH_KB_REQUEST, fetchKnowledgeBases);
  yield takeLatest(CREATE_KB_REQUEST, createKnowledgeBase);
  yield takeLatest(DELETE_KB_REQUEST, deleteKnowledgeBase);
  yield takeLatest(UPDATE_KB_REQUEST, updateKnowledgeBase);
  yield takeLatest(FETCH_KB_FILES_REQUEST, fetchKnowledgeBaseFiles);
  yield takeLatest(FETCH_KB_FILE_CONTENT_REQUEST, fetchFileContent);
  yield takeLatest(UPLOAD_FILES_REQUEST, uploadFiles);
  yield takeLatest(DELETE_KB_FILE_REQUEST, deleteKnowledgeBaseFile);
  yield takeLatest(FETCH_KB_SITES_REQUEST, fetchKnowledgeBaseSites);
  yield takeLatest(FETCH_SITE_CONTENT_REQUEST, fetchSiteContent);
  yield takeLatest(ADD_KB_SITE_REQUEST, addKnowledgeBaseSite);
  yield takeLatest(DELETE_KB_SITE_REQUEST, deleteKnowledgeBaseSite);
  yield takeLatest(DOWNLOAD_FILE_REQUEST, downloadFileSaga);
}

export default knowledgeBaseSaga;
